import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const TermsOfUse = () => {
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const { from, step, formData } = location.state || { from: '/signup', step: 1, formData: null };

  useEffect(() => {
    setMounted(true);
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className={`max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 transform transition-all duration-800 ease-out ${
        mounted ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      }`}>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Terms of Use</h1>
          <Link 
            to={from}
            state={{ step: step, formData: formData }}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Signup
          </Link>
        </div>

        <div className="prose prose-green max-w-none">
          <p className="text-sm text-gray-500 mb-4">
            Effective Date: December 24, 2025 | Last Updated: December 24, 2025
          </p>

          <div className="bg-green-50 p-4 rounded-lg mb-6">
            <p className="text-gray-700">
              Welcome to Pengravity CSS/PMS (“we,” “us,” “our,” or “Pengravity”). By accessing or using our website <a href="https://www.pengravity.com" className="text-green-600 hover:underline">www.pengravity.com</a> and our Learning Management System (the “LMS”), you agree to comply with these Terms of Use. Please read them carefully.
            </p>
            <p className="text-gray-700 mt-2">
              If you have any questions, contact us at: <span className="font-semibold">Email: pengravity@gmail.com</span> | <span className="font-semibold">Phone/WhatsApp: +92 325 2666328</span>
            </p>
          </div>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">1. Acceptance of Terms</h2>
            <p className="text-gray-600">By using our platform, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use, as well as any other policies referenced herein, including our Privacy Policy.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">2. Eligibility</h2>
            <p className="text-gray-600">Pengravity CSS/PMS is intended for users 18 years and above. By using the platform, you confirm that you meet this age requirement.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">3. User Accounts</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>To access certain features, you may need to create an account and provide accurate, complete, and up-to-date information.</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>You agree to notify us immediately of any unauthorized use of your account.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">4. Use of the Platform</h2>
            <p className="text-gray-600">You agree to use Pengravity CSS/PMS for lawful and educational purposes only. Prohibited activities include:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-1 mt-2">
              <li>Copying, distributing, or sharing content without permission</li>
              <li>Using the platform to engage in unlawful, harmful, or fraudulent activities</li>
              <li>Attempting to interfere with or disrupt the LMS or related systems</li>
              <li>Impersonating other users or staff members</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">5. Intellectual Property</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>All content, lectures, notes, graphics, and materials on Pengravity CSS/PMS are the intellectual property of Pengravity and protected under Pakistani copyright laws.</li>
              <li>You may not reproduce, distribute, or create derivative works without explicit written permission.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">6. Payments and Refunds</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>All course fees and payments are processed securely through trusted third-party gateways.</li>
              <li>Refunds, if applicable, will be governed by our Refund Policy (please refer to it separately).</li>
              <li>Unauthorized use or tampering with payment systems is strictly prohibited.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">7. Disclaimers</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>While we strive to provide accurate and updated content, Pengravity CSS/PMS does not guarantee completeness, reliability, or results from the use of the platform.</li>
              <li>The platform is provided "as is," and your use is at your own risk.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">8. Limitation of Liability</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>Pengravity CSS/PMS and its affiliates are not liable for any direct, indirect, incidental, or consequential damages arising from your use of the platform.</li>
              <li>This includes, but is not limited to, errors in content, technical failures, or interruptions in service.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">9. Termination</h2>
            <p className="text-gray-600">We may suspend or terminate your access to the LMS without prior notice if you violate these Terms of Use or engage in misconduct.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">10. Changes to Terms</h2>
            <p className="text-gray-600">Pengravity may revise these Terms of Use at any time. Changes will be posted on this page with an updated "Last Updated" date. Continued use of the LMS constitutes acceptance of the revised terms.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">11. Governing Law</h2>
            <p className="text-gray-600">These Terms of Use are governed by and construed in accordance with the laws of Pakistan. Any disputes will be subject to the exclusive jurisdiction of courts in Pakistan.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">12. Contact Information</h2>
            <p className="text-gray-600">For questions or concerns regarding these Terms of Use:</p>
            <div className="bg-gray-50 p-4 rounded-lg mt-2">
              <p className="text-gray-700"><span className="font-semibold">Pengravity Education System</span></p>
              <p className="text-gray-700">Email: pengravity@gmail.com</p>
              <p className="text-gray-700">Phone/WhatsApp: +92 325 2666328</p>
              <p className="text-gray-700">Website: www.pengravity.com</p>
            </div>
          </section>
        </div>

        <div className="mt-8 text-center">
          <Link 
            to={from}
            state={{ step: step, formData: formData }}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-700 transform hover:scale-105 transition-all duration-300"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Signup
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;